import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {useEffect, useRef, useState} from '@wordpress/element';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {type TokenResult, type Payments, type GooglePay, type PaymentRequest} from '@square/web-payments-sdk-types';
import {z} from '@peachpay/checkout-blocks';
import {getErrorString} from '../../../../../../frontend/@shared/ts/error';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {buildOptions} from '../payment-request';

const SettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	fallback_currency: z.string().nullable(),
	supports: z.string().array(),
	square: z.object({
		application_id: z.string(),
		location_id: z.string(),
		country_code: z.string(),
	}),
});

const parsedResult = SettingsSchema.safeParse(getSetting('peachpay_square_googlepay_data'));
if (!parsedResult.success) {
	throw new Error(`Payment method "peachpay_square_googlepay" setup aborted. Invalid settings.\n\n${parsedResult.error.message}`);
}

const settings = parsedResult.data;
const title = decodeEntities(settings.title);
const description = decodeEntities(settings.description);

const iconStyle = {maxHeight: '25px'} as const;
const descriptionStyle = {textAlign: 'left' as const, margin: '0.5rem 0 0'} as const;
const hrStyle = {margin: '0.5rem 0'} as const;
const smallTextStyle = {textAlign: 'left' as const, margin: '0', fontSize: 'smaller'} as const;

if (!window.Square) {
	throw new Error('Payment method "peachpay_square_googlepay" setup aborted. Square SDK not loaded.');
}

let payments: Payments;
try {
	payments = window.Square.payments(settings.square.application_id, settings.square.location_id);
} catch (err) {
	throw new Error(`Payment method "peachpay_square_googlepay" setup aborted. Could not create Payments instance.\n\n${getErrorString(err)}`);
}

/**
 * Component to render the payment method label
 */
function Label(props: PaymentMethodInterface) {
	const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
	return (
		<>
			<PaymentMethodLabel text={title} />
			<PaymentMethodIcons className='pp-label-icon' icons={[{
				id: 'peachpay_square_googlepay',
				src: settings.icon,
				alt: title,
			}]} />
		</>
	);
}

/**
 * Component to render the googlepay body.
 */
function Content({billing, cartData, eventRegistration, emitResponse}: PaymentMethodInterface) {
	const {
		onPaymentSetup,
		onCheckoutFail,
	} = eventRegistration;

	const paymentRequestRef = useRef<PaymentRequest | null>(null);
	const googlePayRef = useRef<GooglePay | null>(null);
	const [initError, setInitError] = useState<string | null>(null);

	/**
	 * Setup the Square PaymentRequest and GooglePay instance. This is only done
     * once on component mount.
	 */
	useEffect(() => {
		const setupGooglePay = async () => {
			paymentRequestRef.current = payments.paymentRequest(buildOptions({
				merchantCountry: settings.square.country_code,
				currency: billing.currency.code,
				items: cartData.cartItems,
				summary: billing.cartTotalItems,
				total: billing.cartTotal,
				displayPricesIncludingTax: billing.displayPricesIncludingTax,
			}));

			googlePayRef.current = await payments.googlePay(paymentRequestRef.current);
		};

		setupGooglePay()
			.catch((err: unknown) => {
				const message = err instanceof Error ? err.message : __('Google Pay initialization failed.', 'peachpay-for-woocommerce');
				setInitError(message);
			});

		return () => {
			googlePayRef.current?.destroy()
				.catch(console.error);
		};
	}, []);

	/**
     * Make sure the Payment request object stays up to date so the google pay
     * sheet displays the correct information to the customer.
     */
	useEffect(() => {
		paymentRequestRef.current?.update(buildOptions({
			merchantCountry: settings.square.country_code,
			currency: billing.currency.code,
			items: cartData.cartItems,
			summary: billing.cartTotalItems,
			total: billing.cartTotal,
			displayPricesIncludingTax: billing.displayPricesIncludingTax,
		}));
	}, [
		cartData.cartItems,
		billing.currency.code,
		billing.cartTotalItems,
		billing.cartTotal.label,
		billing.cartTotal.value,
		billing.displayPricesIncludingTax,
	]);

	/**
	 * Handle payment tokenization right before the order is submitted.
	 */
	useEffect(() => {
		const unsubscribe = onPaymentSetup(async () => {
			if (!googlePayRef.current) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('Missing ref to Square Google Pay element. Please try again or use a different payment method.', 'peachpay-for-woocommerce'),
				};
			}

			let tokenResult: TokenResult | null = null;
			try {
				tokenResult = await googlePayRef.current.tokenize();
				if (tokenResult.status !== 'OK' || !tokenResult.token) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message: tokenResult.status === 'Cancel'
							? __('Payment was cancelled', 'peachpay-for-woocommerce')
							: ((tokenResult.errors ?? []) as Array<{message: string}>).map(err => err.message).join('<br/>')
								|| __('Google Pay tokenization failed. Please try again or use a different payment method.', 'peachpay-for-woocommerce'),
					};
				}
			} catch (err) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: getErrorString(err),
				};
			}

			return {
				type: emitResponse.responseTypes.SUCCESS,
				meta: {
					paymentMethodData: {
						peachpay_square_source_id: tokenResult.token,
					},
				},
			};
		});
		return unsubscribe;
	}, [onPaymentSetup]);

	/**
	 * Handle checkout failure after the order was submitted. Failure may or may
	 * not be payment related.
	 */
	useEffect(() => {
		const unsubscribe = onCheckoutFail(async (data: CheckoutAfterProcessingWithErrorEventData) => ({
			type: emitResponse.responseTypes.ERROR,
			message: (data.processingResponse?.paymentDetails?.['message']) ?? __('An unknown error occured while processing the payment.', 'peachpay-for-woocommerce'),
			messageContext: emitResponse.noticeContexts.PAYMENTS,
		}));
		return unsubscribe;
	}, [onCheckoutFail]);

	/**
	 * If the payment method does not support the current currency we will show
	 * a fallback button to allow changing the currency.
	 *
	 * Note: Due to how our currency switcher works the `fallback_currency` setting
	 * is only defined when the payment does not support the current currency and
	 * does not change after page load.
	 */
	if (settings.fallback_currency) {
		return <CurrencyFallback name={title} currency={settings.fallback_currency} />;
	}

	if (initError) {
		return <p>{initError}</p>;
	}

	return (
		<>
			<img style={iconStyle} src={settings.icon} alt=''></img>
			<p style={descriptionStyle}>{title} {__('selected for checkout.', 'peachpay-for-woocommerce')}</p>
			<hr style={hrStyle}/>
			<p style={smallTextStyle}>{description}</p>
		</>
	);
}

registerPaymentMethod({
	name: 'peachpay_square_googlepay',
	ariaLabel: title,
	canMakePayment(data) {
		return data.paymentMethods.includes('peachpay_square_googlepay');
	},
	// @ts-expect-error WC will clone these components and pass the props when used.
	label: <Label />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	content: <Content />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	edit: <Content />,
	supports: {
		features: settings.supports,
	},
});
